import i18n from "@/i18n.ts";
import { ApiSorting } from "@/types/api.types.ts";
import { ApiAIProcessingStatus, ApiAISummaryResult } from "@/types/ai/ai.types.ts";
import { ApiExportFormat } from "@/types/analytics/analytics.types.ts";
import { ApiComment } from "@/types/comment.types.ts";
import { ApiCreationSource } from "@/types/common.types.ts";
import { ApiEmployee, ApiEmployeeShort } from "@/types/employees/employee.types.ts";
import { ApiFilterBase, ApiPagedData } from "@skailer-com/uikit/core";
import {
  ApiFormAggregationResult,
  ApiFormElementAggregation,
} from "@/types/forms/form-result.types.ts";
import {
  ApiForm,
  ApiFormShort,
  ApiFormStats,
  ApiFormViewElement,
} from "@/types/forms/form-view.types.ts";
import {
  ApiFormParticipantStatus,
  ApiFormResultAnswerAnonymous,
  ApiFormScoreMark,
} from "@/types/forms/form.types.ts";
import { ApiGoal } from "@/types/goals.types.ts";
import { ReminderSettings } from "@/types/reminder.types.ts";
import { Rule, RuleGroup, RuleGroupShort } from "@/types/rule.types.ts";
import { ApiWorkflowShort } from "@/types/workflows/workflow.types.ts";
import { SelectItem } from "@skailer-com/uikit";

export enum ApiPerformanceReviewStatus {
  draft = "draft",
  pending_launch = "pending_launch",
  running = "running",
  paused = "paused",
  completed = "completed",
}

export const getApiPerformanceReviewStatusTitle = (status: ApiPerformanceReviewStatus): string => {
  switch (status) {
    case ApiPerformanceReviewStatus.draft:
      return i18n.t("common:draft");
    case ApiPerformanceReviewStatus.pending_launch:
      return i18n.t("common:status_pending_launch");
    case ApiPerformanceReviewStatus.running:
      return i18n.t("common:status_running");
    case ApiPerformanceReviewStatus.paused:
      return i18n.t("common:status_paused");
    case ApiPerformanceReviewStatus.completed:
      return i18n.t("common:status_completed");
  }
};

export interface ApiPerformanceReviewsFilter extends ApiFilterBase {
  text?: string;
  statuses?: ApiPerformanceReviewStatus[];
  creation_source?: ApiCreationSource | null;
}

export interface ApiPerformanceReviewReviewerStats {
  total_count: number;
  completed_count: number;
  added_count: number;
  invited_count: number;
  in_progress_count: number;
  unsolicited_count: number;
}

export interface ApiPerformanceReviewShort {
  id: number;
  title: string;
  emoji: string | null;
  status: ApiPerformanceReviewStatus;
  date_starts_at: Date | null;
  date_ends_at: Date | null;
  date_completed_at: Date | null;
}

export interface ApiPerformanceReview extends ApiPerformanceReviewShort {
  reviewer_stats: ApiPerformanceReviewReviewerStats;
  reviewer_types: ApiPerformanceReviewReviewerType[];
}

export interface ApiPerformanceReviewSearchItem {
  id: number;
  title: string;
  emoji: string | null;
  status: ApiPerformanceReviewStatus;
  date_starts_at: Date | null;
  date_ends_at: Date | null;
  owners: ApiEmployeeShort[];
  created_by_workflow: ApiWorkflowShort | null;
  reviewer_stats: ApiPerformanceReviewReviewerStats;
}

export enum ApiPerformanceReviewReviewerTypes {
  myself = "myself",
  manager = "manager",
  upward = "upward",
  peer = "peer",
  custom = "custom",
}

export interface PerformanceReviewReviewerTypeItem extends SelectItem {
  shortTitle: string;
}

export const PerformanceReviewReviewerTypeItems = () => {
  return [
    {
      id: ApiPerformanceReviewReviewerTypes.myself,
      title: i18n.t("performance_review:review_type_self"),
      shortTitle: i18n.t("performance_review:review_type_self_short"),
    },
    {
      id: ApiPerformanceReviewReviewerTypes.manager,
      title: i18n.t("performance_review:review_type_manager"),
      shortTitle: i18n.t("performance_review:review_type_manager_short"),
    },
    {
      id: ApiPerformanceReviewReviewerTypes.upward,
      title: i18n.t("performance_review:review_type_upward"),
      shortTitle: i18n.t("performance_review:review_type_upward_short"),
    },
    {
      id: ApiPerformanceReviewReviewerTypes.peer,
      title: i18n.t("performance_review:review_type_peer"),
      shortTitle: i18n.t("performance_review:review_type_peer_short"),
    },
  ] as PerformanceReviewReviewerTypeItem[];
};

export interface ApiPerformanceReviewReviewerTypeSettingAccess {
  is_allow_manager_early_access: boolean;
  is_allow_manager_sharing: boolean;
  is_allow_edit_after_submit?: boolean | null;
}

export interface ApiReviewerTypeSettingNotifications {
  is_notify_owners_on_complete: boolean;
}

export interface ApiPerformanceReviewReviewerTypeEdit {
  id: number;
  title: string | null;
  type: ApiPerformanceReviewReviewerTypes;
  form: ApiFormShort | null;
  template_title: string | null;
  setting_access: ApiPerformanceReviewReviewerTypeSettingAccess;
  setting_notifications: ApiReviewerTypeSettingNotifications;
  anonymous_threshold: number;
  is_allow_to_invite: boolean;
  is_allow_manager_to_invite: boolean;
  is_allow_feedback_from_anyone: boolean;
  rating_form_element_ids: number[];
  is_rating_all_scales: boolean;
  is_auto_share: boolean;
  auto_share_timing: ApiPerformanceReviewAutoShareTiming | null;
  invitation_subject: string | null;
  invitation_body: string | null;
}

export enum ApiPerformanceReviewInvitationMode {
  common = "common",
  by_reviewer_type = "by_reviewer_type",
}

export interface ApiPerformanceReviewEdit {
  id: number;
  title: string;
  description: string | null;
  emoji: string | null;
  participants_rule_groups?: RuleGroup[] | null;
  participants_rules?: Rule[] | null;
  owners: ApiEmployeeShort[];
  date_starts_at: Date | null;
  date_ends_at: Date | null;
  status: ApiPerformanceReviewStatus;
  invitation_from_employee: ApiEmployeeShort;
  invitation_subject: string | null;
  invitation_body: string | null;
  invitation_mode: ApiPerformanceReviewInvitationMode;
  reviewer_types: ApiPerformanceReviewReviewerTypeEdit[];
  is_same_rating: boolean;
  rating_form_element_ids: number[];
  is_rating_all_scales: boolean;
  is_avg_reviewer_type_rating: boolean;
  included_in_ratings_reviewer_type_ids: number[];
  use_ai: boolean;
  reminder_settings: ReminderSettings | null;
}

export interface ApiPerformanceReviewUpdateDetails {
  title: string;
  description: string | null;
  emoji: string | null;
  participants_rule_groups: RuleGroupShort[];
  owner_ids?: number[];
  date_starts_at: Date | null;
  date_ends_at: Date | null;
  use_ai: boolean;
}

export function getPerformanceReviewParticipantGroups(
  review: ApiPerformanceReviewEdit
): RuleGroup[] {
  if (review.participants_rule_groups != null) return review.participants_rule_groups;
  const rules = review.participants_rules ?? [];
  return rules.length > 0 ? [{ rules }] : [];
}

export interface ApiPerformanceReviewSaveReviewerType {
  id: number | null;
  title: string | null;
  type: ApiPerformanceReviewReviewerTypes;
}

export enum ApiPerformanceReviewAutoShareTiming {
  after_end = "after_end",
  immediately = "immediately",
}

export interface ApiPerformanceReviewSaveReviewerTypes {
  types: ApiPerformanceReviewSaveReviewerType[];
}

export interface ApiPerformanceReviewReviewerTypeUpdateForm {
  create_new_form?: boolean | null;
  form_template_id?: number | null;
  reset_form?: boolean | null;
}

export interface ApiPerformanceReviewUpdateReviewerTypeSetting {
  id: number;
  setting_access: ApiPerformanceReviewReviewerTypeSettingAccess;
  setting_notifications: ApiReviewerTypeSettingNotifications;
  anonymous_threshold: number;
  is_allow_to_invite: boolean;
  is_allow_manager_to_invite: boolean;
  is_allow_feedback_from_anyone: boolean;
  is_auto_share: boolean;
  auto_share_timing: ApiPerformanceReviewAutoShareTiming | null;
}

export interface ApiPerformanceReviewUpdateReviewerTypeSettings {
  settings: ApiPerformanceReviewUpdateReviewerTypeSetting[];
}

export interface ApiPerformanceReviewUpdateReviewerTypeInvitation {
  id: number;
  invitation_subject: string | null;
  invitation_body: string | null;
}

export interface ApiPerformanceReviewUpdateInvitation {
  invitation_from_employee_id: number;
  invitation_mode: ApiPerformanceReviewInvitationMode;
  invitation_subject: string | null;
  invitation_body: string | null;
  reviewer_types: ApiPerformanceReviewUpdateReviewerTypeInvitation[];
}

export interface ApiPerformanceReviewReviewerTypeUpdateRating {
  id: number;
  rating_form_element_ids: number[];
  is_rating_all_scales: boolean;
}

export interface ApiPerformanceReviewUpdateRatings {
  is_same_rating: boolean;
  rating_form_element_ids: number[];
  is_rating_all_scales: boolean;
  reviewer_type_ratings: ApiPerformanceReviewReviewerTypeUpdateRating[];
  is_avg_reviewer_type_rating: boolean;
  included_in_ratings_reviewer_type_ids: number[];
}

export interface ApiPerformanceReviewReviewerPreviewCount {
  reviewer_type: ApiPerformanceReviewReviewerTypes;
  count: number;
}

export interface ApiPerformanceReviewRespondView {
  id: number;
  title: string;
  description: string | null;
  emoji: string | null;
  status: ApiPerformanceReviewStatus;
  date_starts_at: Date | null;
  date_ends_at: Date | null;
  participant: ApiEmployee;
  reviewer_id: number;
  reviewer_type_id: number;
  reviewer_status: ApiFormParticipantStatus;
  reviewer_type: ApiPerformanceReviewReviewerTypes;
  reviewer_type_title: string | null;
  is_anonymous: boolean;
  can_edit_results: boolean;
  form_stats: ApiFormStats;
  comments: ApiComment[] | null;
}

export interface ApiPerformanceReviewParticipantsSummaryFilter extends ApiFilterBase {
  text?: string;
  division_ids?: number[];
  team_ids?: number[];
  location_ids?: number[];
  employment_type_ids?: number[];
  sorting?: ApiSorting;
  job_profile_ids?: number[];
  job_role_ids?: number[];
  department_ids?: number[];
}

export interface ApiPerformanceReviewReviewerType {
  id: number;
  title: string | null;
  type: ApiPerformanceReviewReviewerTypes;
  datetime_completed_at?: Date | null;
  respondent_uuid?: string | null;
  reviewer_id?: number;
  can_edit_results?: boolean;
}

export interface ApiPerformanceReviewReviewersSummary {
  total_count: number;
  completed_count: number;
  added_count: number;
  invited_count: number;
  in_progress_count: number;
  rating: ApiFormScaleScore;
  // score_marks: ApiFormScoreMark[];
}

export interface ApiPerformanceReviewReviewerTypeSummarySearchItem {
  reviewer_type: ApiPerformanceReviewReviewerType;
  summary: ApiPerformanceReviewReviewersSummary;
  is_below_anonymity_threshold: boolean;
}

export interface ApiPerformanceReviewParticipantSummarySearchItem {
  id: number;
  employee: ApiEmployee;
  assigned_manager: ApiEmployeeShort | null;
  overall_summary: ApiPerformanceReviewReviewersSummary;
  reviewer_type_summaries: ApiPerformanceReviewReviewerTypeSummarySearchItem[];
  share_setting: ApiPerformanceReviewParticipantShareSetting | null;
  datetime_last_reminder?: Date | null;
}

export interface ApiPerformanceReviewParticipantsSummaryConfig {
  reviewer_types: ApiPerformanceReviewReviewerType[];
}

export interface ApiPerformanceReviewParticipantsSummary {
  config: ApiPerformanceReviewParticipantsSummaryConfig;
  result: ApiPagedData<ApiPerformanceReviewParticipantSummarySearchItem>;
}

export interface ApiPerformanceReviewReviewerSummary {
  id: number | null;
  respondent_uuid: string | null;
  status: ApiFormParticipantStatus | null;
  employee: ApiEmployee | null;
  is_unsolicited: boolean | null;
  rating: ApiFormScaleScore;
}

export interface ApiPerformanceReviewReviewerTypeSummary {
  reviewer_type: ApiPerformanceReviewReviewerType;
  summary: ApiPerformanceReviewReviewersSummary;
  reviewers_summaries: ApiPerformanceReviewReviewerSummary[];
  is_below_anonymity_threshold: boolean;
  ai_summary: string | null;
  ai_summary_short: string | null;
}

export interface ApiPerformanceReviewParticipantSummary {
  id: number;
  employee: ApiEmployee;
  summary: ApiPerformanceReviewReviewersSummary;
  reviewer_type_summaries: ApiPerformanceReviewReviewerTypeSummary[];
}

export interface ApiPerformanceReviewReviewerTypeAggregatedResults {
  form: ApiForm;
  results: ApiFormAggregationResult;
}

export interface ApiPerformanceReviewResultsFilter extends ApiFilterBase {
  performance_review_id: number;
  reviewer_type_id: number | null;
  form_element_ids: number[];
  participant_id?: number | null;
  reference_id?: number | null;
  answer_select_option_ids?: number[];
  answer_scale_option_ids?: number[];
  selectable_option_ids?: number[];
  department_ids?: number[];
  manager_ids?: number[];
}

export interface ApiPerformanceReviewResultSearchItem {
  answer: ApiFormResultAnswerAnonymous | null;
  employee: ApiEmployee | null;
  goal: ApiGoal | null;
  is_unsolicited: boolean | null;
}

export interface ApiPerformanceReviewReviewerStatsByPeriod {
  date_start: Date;
  date_end: Date;
  total_count: number;
}

export interface ApiPerformanceReviewReviewerStatsBase {
  total_count: number;
  completed_count: number;
  added_count: number;
  invited_count: number;
  in_progress_count: number;
  completed_by_periods: ApiPerformanceReviewReviewerStatsByPeriod[];
}

export interface ApiPerformanceReviewReviewerTypeReviewerStats extends ApiPerformanceReviewReviewerStatsBase {
  reviewer_type_id: number;
}

export interface ApiPerformanceReviewReviewerStatsFull extends ApiPerformanceReviewReviewerStatsBase {
  performance_review_id: number;
  date_start: Date;
  date_end: Date;
  stat_by_reviewer_types: ApiPerformanceReviewReviewerTypeReviewerStats[];
}

export interface ApiPerformanceReviewParticipantReportFilter {
  reviewer_type_id?: number;
}

export interface ApiPerformanceReviewParticipantReportConfig {
  reviewer_types: ApiPerformanceReviewReviewerType[];
}

export interface ApiFormScaleScore {
  max_scale_score: number | null;
  avg_scale_score: number | null;
  avg_score_mark: ApiFormScoreMark | null;
}

export interface ApiPerformanceReviewParticipantSectionsReportDataSection {
  title: string;
  overall_scale_score: ApiFormScaleScore;
  reviewer_type_scale_scores: ApiFormScaleScore[];
}

export interface ApiPerformanceReviewParticipantSectionsReportDataTotal {
  overall_scale_score: ApiFormScaleScore;
  reviewer_type_scale_scores: ApiFormScaleScore[];
}

export interface ApiPerformanceReviewParticipantSectionsReportData {
  sections: ApiPerformanceReviewParticipantSectionsReportDataSection[];
  total: ApiPerformanceReviewParticipantSectionsReportDataTotal;
}

export interface ApiPerformanceReviewParticipantSectionsReport {
  config: ApiPerformanceReviewParticipantReportConfig;
  report: ApiPerformanceReviewParticipantSectionsReportData;
  modified_reviewer_type_ids: number[];
}

export interface ApiFormMergedElement {
  element_ids: number[];
  element: ApiFormViewElement;
  result_aggregation: ApiFormElementAggregation | null;
  scale_score: ApiFormScaleScore;
  // score_marks: ApiFormScoreMark[];
}

export interface ApiFormMergedSection {
  title: string | null;
  elements: ApiFormMergedElement[];
  scale_score: ApiFormScaleScore;
}

export interface ApiPerformanceReviewParticipantElementsReport {
  sections: ApiFormMergedSection[];
}

export interface ApiPerformanceReviewParticipantReportData {
  id: number;
  performance_review: ApiPerformanceReviewShort;
  employee: ApiEmployee;
  summary: ApiPerformanceReviewReviewersSummary;
  reviewer_type_summaries: ApiPerformanceReviewReviewerTypeSummary[];
  sections_report: ApiPerformanceReviewParticipantSectionsReport | null;
  elements_report: ApiPerformanceReviewParticipantElementsReport;
  is_below_anonymity_threshold: boolean;
  ai_processing_status: ApiAIProcessingStatus;
  ai_summary: ApiAISummaryResult | null;
}

export interface ApiPerformanceReviewReviewerShareSetting {
  respondent_uuid: string;
  status: ApiFormParticipantStatus | null;
  employee: ApiEmployee | null;
  scale_score: ApiFormScaleScore;
  is_feedback_shared: boolean;
}

export interface ApiPerformanceReviewReviewerTypeShareSetting {
  reviewer_type: ApiPerformanceReviewReviewerType;
  is_below_anonymity_threshold: boolean;
  reviewers_share_settings: ApiPerformanceReviewReviewerShareSetting[];
}

export interface ApiPerformanceReviewParticipantShareSetting {
  is_shared: boolean;
  reviewer_type_share_settings: ApiPerformanceReviewReviewerTypeShareSetting[];
}

export interface ApiPerformanceReviewMyResponse {
  reviewer_id: number;
  is_modified: boolean;
}

export interface ApiPerformanceReviewParticipantReport {
  config: ApiPerformanceReviewParticipantReportConfig;
  report: ApiPerformanceReviewParticipantReportData;
  share_setting: ApiPerformanceReviewParticipantShareSetting | null;
  my_response: ApiPerformanceReviewMyResponse | null;
}

export interface ApiPerformanceReviewReviewer {
  id: number;
  status: ApiFormParticipantStatus | null;
  comment: ApiComment | null;
  employee: ApiEmployee;
  is_unsolicited: boolean;
}

export interface ApiPerformanceReviewParticipantReviewers {
  employee: ApiEmployee;
  reviewer_type: ApiPerformanceReviewReviewerType;
  reviewers: ApiPerformanceReviewReviewer[];
  exclude_employee_ids: number[];
}

export interface ApiPerformanceReviewParticipantReviewersSave {
  employees: ApiPerformanceReviewParticipantReviewersSaveEmployee[];
}

export interface ApiPerformanceReviewParticipantReviewersSaveEmployee {
  employee_id: number;
  comment: string | null;
}

export interface ApiPerformanceReviewParticipantFilter extends ApiFilterBase {
  text?: string;
}

export interface ApiPerformanceReviewParticipant {
  employee: ApiEmployee;
  review: ApiPerformanceReviewShort;
  reviewer_types: ApiPerformanceReviewReviewerType[];
}

export interface ApiPerformanceReviewParticipantFeedback {
  employee: ApiEmployee;
  participants: ApiPerformanceReviewParticipant[];
}

export interface ApiPerformanceReviewParticipantShareSettingUpdate {
  is_shared: boolean;
  is_share_all_feedback: boolean;
  share_respondent_uuids: string[];
}

export enum ApiParticipantChartAxisType {
  overallReview = "overall_review",
  reviewerType = "reviewer_type",
}

export enum ApiParticipantChartDataPointSize {
  smallest = "smallest",
  small = "small",
  medium = "medium",
  large = "large",
  largest = "largest",
}

export interface ApiParticipantChartSettingAxisConfig {
  type: ApiParticipantChartAxisType;
  reviewer_type_id?: number | null;
}

export interface ApiPerformanceReviewParticipantChartSetting {
  x_axis: ApiParticipantChartSettingAxisConfig;
  y_axis: ApiParticipantChartSettingAxisConfig;
}

export interface ApiPerformanceReviewParticipantChartFilter extends ApiFilterBase {
  text?: string;
  employee_ids?: number[];
  job_profile_ids?: number[];
  job_role_ids?: number[];
  department_ids?: number[];
  division_ids?: number[];
  location_ids?: number[];
  employment_contract_ids?: number[];
  employment_type_ids?: number[];
}

export interface ApiPerformanceReviewParticipantChartPayload {
  setting: ApiPerformanceReviewParticipantChartSetting;
  filter: ApiPerformanceReviewParticipantChartFilter;
}

export interface ApiParticipantChartDataPoint {
  x: number;
  y: number;
  size: ApiParticipantChartDataPointSize;
  employee_ids: number[];
  employee?: ApiEmployeeShort;
}

export interface ApiParticipantChartDataAxisMeta {
  min: number;
  max: number;
}

export interface ApiParticipantChartData {
  points: ApiParticipantChartDataPoint[];
  x_axis: ApiParticipantChartDataAxisMeta;
  y_axis: ApiParticipantChartDataAxisMeta;
}

export interface ApiParticipantChartConfigAxisConfig {
  type: ApiParticipantChartAxisType;
  reviewer_type: ApiPerformanceReviewReviewerType | null;
}

export interface ApiParticipantChartConfig {
  x_axis: ApiParticipantChartConfigAxisConfig;
  y_axis: ApiParticipantChartConfigAxisConfig;
}

export interface ApiPerformanceReviewParticipantChart {
  data: ApiParticipantChartData;
  config: ApiParticipantChartConfig;
}

export interface ApiParticipantChartAxisValue {
  normalized_value: number;
  scale_score: ApiFormScaleScore | null;
}

export interface ApiParticipantChartRating {
  value: number;
  max: number;
}

export interface ApiPerformanceReviewParticipantChartSearchItem {
  id: number;
  employee: ApiEmployee;
  rating: ApiParticipantChartRating;
  x_axis: ApiParticipantChartAxisValue | null;
  y_axis: ApiParticipantChartAxisValue | null;
}

export interface ApiPerformanceReviewParticipantChartSearch {
  config: ApiParticipantChartConfig;
  result: ApiPagedData<ApiPerformanceReviewParticipantChartSearchItem>;
}

export interface ApiPerformanceReviewInviteView {
  review: ApiPerformanceReviewShort;
  reviewer_types: ApiPerformanceReviewReviewerType[];
  has_invitations_from_user: boolean;
}

export interface ApiPerformanceReviewParticipantViewFilter extends ApiFilterBase {
  text?: string;
}

export interface ApiPerformanceReviewParticipantView {
  review: ApiPerformanceReviewShort;
  scale_score: ApiFormScaleScore;
}

export interface ApiPerformanceReviewExportPayload {
  format: ApiExportFormat;
}

export interface ApiPRReviewReopen {
  date_ends_at: Date | null;
}

export interface ApiPerformanceReviewParticipantSearchFilter extends ApiFilterBase {
  text: string;
}
